import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useData, computeName } from '../contexts/DataContext';
import { uploadFile } from '../services/publitio';
import { getProfileImageUrl } from '../utils/profileImage';
import { DEFAULT_AVATAR_URL } from '../utils/constants';
import Navbar from '../components/layout/Navbar';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch, FiEdit, FiTrash2, FiMail, FiCalendar, FiUpload } = FiIcons;

// Team ID options
const TEAM_IDS = [
  { id: 'emd_rodriguez', name: 'EMD Rodriguez' },
  { id: 'md_garcia', name: 'MD Garcia' },
  { id: 'md_samaniego', name: 'MD Samaniego' }
];

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const sanitizeNamePart = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  const trimmed = String(value).trim();
  return trimmed;
};

const splitFullName = (fullName) => {
  if (!fullName) {
    return { first: '', middle: '', last: '' };
  }

  const parts = String(fullName)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return { first: '', middle: '', last: '' };
  }

  if (parts.length === 1) {
    return { first: parts[0], middle: '', last: '' };
  }

  if (parts.length === 2) {
    return { first: parts[0], middle: '', last: parts[1] };
  }

  const first = parts.shift();
  const last = parts.pop();
  const middle = parts.join(' ');

  return {
    first: first ?? '',
    middle,
    last: last ?? ''
  };
};

const getStructuredFields = (record) => {
  const structured = record?.structuredFields ?? record?.structured_fields;
  return isPlainObject(structured) ? structured : {};
};

const getUserNameDetails = (record) => {
  if (!record) {
    return { firstName: '', middleName: '', lastName: '', fullName: '' };
  }

  const structured = getStructuredFields(record);

  let firstName = sanitizeNamePart(
    record?.firstName ?? structured.firstName ?? record?.first_name ?? structured.first_name
  );
  let middleName = sanitizeNamePart(
    record?.middleName ?? structured.middleName ?? record?.middle_name ?? structured.middle_name
  );
  let lastName = sanitizeNamePart(
    record?.lastName ?? structured.lastName ?? record?.last_name ?? structured.last_name
  );

  const fallbackFullName = sanitizeNamePart(
    structured.fullName ?? structured.full_name ?? structured.name ?? record?.name
  );

  if ((!firstName || !lastName) && fallbackFullName) {
    const parsed = splitFullName(fallbackFullName);
    firstName = firstName || parsed.first;
    middleName = middleName || parsed.middle;
    lastName = lastName || parsed.last;
  }

  const fullName = computeName(firstName, middleName, lastName) || fallbackFullName;

  return { firstName, middleName, lastName, fullName };
};

const toSnakeKey = (key) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const buildStructuredNameFields = (baseFields, nameDetails, fullName) => {
  const structured = {
    ...(isPlainObject(baseFields) ? baseFields : {})
  };

  const setOrDelete = (key, value) => {
    const snakeKey = toSnakeKey(key);
    if (value) {
      structured[key] = value;
      if (snakeKey !== key) {
        delete structured[snakeKey];
      }
    } else {
      delete structured[key];
      if (snakeKey !== key) {
        delete structured[snakeKey];
      }
    }
  };

  setOrDelete('firstName', nameDetails.firstName);
  setOrDelete('middleName', nameDetails.middleName);
  setOrDelete('lastName', nameDetails.lastName);
  if (fullName) {
    setOrDelete('fullName', fullName);
    setOrDelete('name', fullName);
  } else {
    setOrDelete('fullName', null);
    setOrDelete('name', null);
  }

  return structured;
};

const UserManagement = () => {
  const { user } = useAuth();
  const { users, addUser, updateUser, deleteUser, addDocument } = useData();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    role: '',
    teamId: '',
    agentCode: '',
    avatar: '',
    profileImageId: '',
    profileImageUrl: '',
    status: 'active'
  });
  const [imagePreview, setImagePreview] = useState(null);

  const searchLower = searchTerm.toLowerCase();
  const enrichedUsers = users.map((userRecord) => ({
    user: userRecord,
    nameDetails: getUserNameDetails(userRecord)
  }));

  const filteredUsers = enrichedUsers.filter(({ user: userRecord, nameDetails }) => {
    const fullNameLower = (nameDetails.fullName || '').toLowerCase();
    const firstNameLower = (nameDetails.firstName || '').toLowerCase();
    const lastNameLower = (nameDetails.lastName || '').toLowerCase();
    const matchesSearch =
      fullNameLower.includes(searchLower) ||
      firstNameLower.includes(searchLower) ||
      lastNameLower.includes(searchLower) ||
      (userRecord.email || '').toLowerCase().includes(searchLower) ||
      (userRecord.agentCode || '').toLowerCase().includes(searchLower);

    if (user?.role === 'manager') {
      return matchesSearch && (userRecord.teamId === user.teamId || userRecord.role === 'client');
    }
    return matchesSearch;
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Generate a default agent code if needed for non-client roles
    let agentCode = formData.agentCode;
    if (formData.role !== 'client' && !agentCode) {
      const prefix = formData.role === 'admin' ? 'ADM' :
                    formData.role === 'manager' ? 'MGR' : 'FP';
      agentCode = `${prefix}${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
    }

    const nameDetails = {
      firstName: sanitizeNamePart(formData.firstName),
      middleName: sanitizeNamePart(formData.middleName),
      lastName: sanitizeNamePart(formData.lastName)
    };
    const computedFullName = computeName(
      nameDetails.firstName,
      nameDetails.middleName,
      nameDetails.lastName
    );
    const existingNameDetails = selectedUser ? getUserNameDetails(selectedUser) : { fullName: '' };
    const finalFullName = computedFullName || existingNameDetails.fullName || '';
    const baseStructuredFields = selectedUser ? getStructuredFields(selectedUser) : {};
    const structuredFields = buildStructuredNameFields(baseStructuredFields, nameDetails, finalFullName);

    const userData = {
      name: finalFullName,
      email: formData.email,
      role: formData.role,
      team_id: formData.teamId,
      agent_code: agentCode,
      status: formData.status
    };

    if (Object.keys(structuredFields).length > 0) {
      userData.structuredFields = structuredFields;
    }

    if (formData.profileImageId || formData.profileImageUrl) {
      userData.profileImageId = formData.profileImageId;
      userData.profileImageUrl = formData.profileImageUrl;
      userData.avatar = null;
    } else {
      userData.avatar = formData.avatar || DEFAULT_AVATAR_URL;
    }

    if (selectedUser) {
      updateUser(selectedUser.id, userData);
      setIsEditModalOpen(false);
    } else {
      addUser(userData);
      setIsAddModalOpen(false);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      role: '',
      teamId: '',
      agentCode: '',
      avatar: '',
      profileImageId: '',
      profileImageUrl: '',
      status: 'active'
    });
    setImagePreview(null);
    setSelectedUser(null);
  };

  const handleEdit = (userToEdit) => {
    setSelectedUser(userToEdit);
    const nameDetails = getUserNameDetails(userToEdit);
    setFormData({
      firstName: nameDetails.firstName,
      middleName: nameDetails.middleName,
      lastName: nameDetails.lastName,
      email: userToEdit.email,
      role: userToEdit.role,
      teamId: userToEdit.teamId || '',
      agentCode: userToEdit.agentCode || '',
      avatar: userToEdit.avatar,
      profileImageId: userToEdit.profileImageId || '',
      profileImageUrl: userToEdit.profileImageUrl || '',
      status: userToEdit.status || 'active'
    });
    setImagePreview(userToEdit.profileImageUrl || userToEdit.avatar);
    setIsEditModalOpen(true);
  };

  const handleDelete = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUser(userId);
    }
  };

  const ROLE_OPTIONS = [
    { value: 'client', label: 'Client' },
    { value: 'representative', label: 'Representative' },
    { value: 'associate', label: 'Associate' },
    { value: 'senior_associate', label: 'Senior Associate' },
    { value: 'marketing_director', label: 'Marketing Director' },
    { value: 'smd', label: 'SMD' },
    { value: 'emd', label: 'EMD' },
    { value: 'admin', label: 'Admin', adminOnly: true }
  ];

  const getRoleDisplayName = (role) => {
    const roleLabels = {
      representative: 'Representative',
      associate: 'Associate',
      senior_associate: 'Senior Associate',
      marketing_director: 'Marketing Director',
      smd: 'SMD',
      emd: 'EMD',
      admin: 'Admin',
      client: 'Client'
    };

    return roleLabels[role] ?? 'Unknown Role';
  };

  const canEditUser = (userToEdit) => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'manager') {
      return userToEdit.teamId === user.teamId || userToEdit.role === 'client';
    }
    return false;
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const upload = await uploadFile(file, 'avatars');
      setImagePreview(upload.url);
      setFormData(prev => ({
        ...prev,
        profileImageId: upload.public_id,
        profileImageUrl: upload.url,
        avatar: ''
      }));
      await addDocument({ name: file.name, clientId: null, publitioId: upload.public_id, url: upload.url });
    } catch (err) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData(prev => ({ ...prev, avatar: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const timePart = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit'
    });
    return `${datePart} ${timePart}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">User Management</h1>
              <p className="text-gray-600">Manage system users and their access permissions</p>
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add User</span>
            </button>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </div>
        </motion.div>

        {/* Users Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="table-container w-full"
        >
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Agent Code</th>
                <th>Team</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(({ user: userItem, nameDetails }) => (
                <tr key={userItem.id}>
                  <td>
                    <div className="flex items-center space-x-3">
                      <img
                        src={getProfileImageUrl(userItem)}
                        alt={nameDetails.fullName || userItem.email || 'User'}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{nameDetails.fullName}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">{userItem.email}</span>
                    </div>
                  </td>
                  <td>
                    <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-xs font-medium">
                      {getRoleDisplayName(userItem.role)}
                    </span>
                  </td>
                  <td>
                    {userItem.agentCode && (
                      <span className="text-gray-600">{userItem.agentCode}</span>
                    )}
                  </td>
                  <td>
                    {userItem.teamId && (
                      <span className="px-3 py-1 bg-secondary-100 text-secondary-800 rounded-full text-xs font-medium">
                        {TEAM_IDS.find(team => team.id === userItem.teamId)?.name || userItem.teamId}
                      </span>
                    )}
                  </td>
                  <td>
                    <StatusBadge status={userItem.status} />
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{formatDate(userItem.createdAt)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      {canEditUser(userItem) && (
                        <button
                          onClick={() => handleEdit(userItem)}
                          className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                        >
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </button>
                      )}
                      {canEditUser(userItem) && userItem.id !== user.id && (
                        <button
                          onClick={() => handleDelete(userItem.id)}
                          className="p-2 text-gray-400 hover:text-danger-600 transition-colors"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Add/Edit User Modal */}
        <Modal
          isOpen={isAddModalOpen || isEditModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            setIsEditModalOpen(false);
            resetForm();
          }}
          title={selectedUser ? 'Edit User' : 'Add New User'}
          size="md"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Picture */}
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <img
                  src={imagePreview || getProfileImageUrl(formData)}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover"
                />
                <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                  <SafeIcon icon={FiUpload} className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-sm text-gray-500">Click the upload button to change profile picture</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="form-input"
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Middle Name
                </label>
                <input
                  type="text"
                  value={formData.middleName}
                  onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
                  className="form-input"
                  placeholder="Enter middle name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="form-input"
                  placeholder="Enter last name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input"
                placeholder="Enter email address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role *
              </label>
              <select
                required
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="form-input"
              >
                <option value="">Select role</option>
                {ROLE_OPTIONS.filter((option) => !option.adminOnly || user?.role === 'admin').map(
                  (option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </div>

            {/* Agent Code - Only for admin, manager, and financial professional */}
            {formData.role && formData.role !== 'client' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agent Code
                </label>
                <input
                  type="text"
                  value={formData.agentCode}
                  onChange={(e) => setFormData({ ...formData, agentCode: e.target.value })}
                  className="form-input"
                  placeholder="Enter agent code (optional - will be auto-generated if empty)"
                />
              </div>
            )}

            {/* Team Selection - Only for admin, manager, and financial professional */}
            {formData.role && formData.role !== 'client' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team *
                </label>
                <select
                  required
                  value={formData.teamId}
                  onChange={(e) => setFormData({ ...formData, teamId: e.target.value })}
                  className="form-input"
                >
                  <option value="">Select team</option>
                  {TEAM_IDS.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="form-input"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsEditModalOpen(false);
                  resetForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                {selectedUser ? 'Update User' : 'Add User'}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default UserManagement;